/**
 * UpcomingTrips.jsx
 * ------------------
 * Lists all upcoming trips for the logged-in user—split into two tabs:
 *  • Trips where the user is the driver
 *  • Trips where the user is a passenger
 *
 * The component mirrors the card layout and styling used in PastTrips so
 * the UI remains consistent across screens. Each card exposes a "More Details"
 * button which opens a modal with additional information from the card data.
 *
 * Features
 * --------
 *  • Tabbed interface (driver / passenger)
 *  • Re-usable card skeleton with coloured stop indicators
 *  • Graceful fallback banner when no trips are returned
 *  • Defensive guards around backend payloads (array / object / null)
 *  • Modal popup with richer trip details
 */

import React, { useState } from "react";
import axios from "axios";
import "./UpcomingTripsScreen.css";
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../../index';
import { formatTime, formatShortDate, formatCompactDateTime } from '../../utils/dateTimeUtils';
import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardContent,
  IonList,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonText,
  IonSpinner,
  IonAlert,
  IonNote,
  IonItem,
  IonChip,
  IonCardHeader,
  IonCardTitle,
  IonAvatar
} from '@ionic/react';
import {
  arrowBackOutline,
  timeOutline,
  closeOutline,
  informationCircleOutline,
  alertCircleOutline,
  navigateOutline,
  locationOutline,
  personOutline,
  carOutline,
  cashOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  copyOutline,
  shield,
  arrowBack
} from 'ionicons/icons';

const UpcomingTrips = () => {
  const [selectedTab, setSelectedTab] = useState('driver');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [popupTripDetails, setPopupTripDetails] = useState(null);

  // New state for the cancellation confirmation popup
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { user, upcomingTripResultsRider, upcomingTripResultsDriver } = location.state;

  console.log("Upcoming Trip Results Rider:", upcomingTripResultsRider);
  console.log("Upcoming Trip Results Driver:", upcomingTripResultsDriver);

  // Ensure we work with arrays even if backend returned a single object or null
  let driverTripsArray = [];
  if (upcomingTripResultsDriver && typeof upcomingTripResultsDriver === 'object') {
    if (upcomingTripResultsDriver.data && upcomingTripResultsDriver.data.upcoming_trips && Array.isArray(upcomingTripResultsDriver.data.upcoming_trips)) {
      driverTripsArray = upcomingTripResultsDriver.data.upcoming_trips;
    } else if (Array.isArray(upcomingTripResultsDriver)) {
      // Fallback for old format
      driverTripsArray = upcomingTripResultsDriver;
    }
  }

  // Handle the new nested structure for rider trips
  let riderTripsArray = [];
  if (upcomingTripResultsRider && typeof upcomingTripResultsRider === 'object') {
    if (upcomingTripResultsRider.rider_trips && Array.isArray(upcomingTripResultsRider.rider_trips)) {
      riderTripsArray = upcomingTripResultsRider.rider_trips;
    } else if (Array.isArray(upcomingTripResultsRider)) {
      // Fallback for old format
      riderTripsArray = upcomingTripResultsRider;
    }
  }

  // Filter out placeholder objects or malformed entries
  const validDriverTrips = driverTripsArray.filter((t) => t && t.driverOriginName);
  const validPassengerTrips = riderTripsArray.filter((t) => t && t.trip_summary && t.trip_summary.RiderStartLocName);

  // Helper to format date using centralized utility
  const formatLocalDate = (ts) => {
    return formatShortDate(ts);
  };

  // Function to format numeric values safely
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    const num = Number(value);
    if (Number.isNaN(num)) return 'N/A';
    return num.toFixed(decimals);
  };

  // Function to calculate estimated earnings for driver trips
  const calculateEstimatedEarnings = (trip) => {
    if (!trip.TotalDistance || !trip.costPerKM) {
      return null;
    }
    
    const distanceKm = trip.TotalDistance / 1000; // Convert meters to kilometers
    const costPerKm = Number(trip.costPerKM);
    const maxPassengers = 3;
    
    if (Number.isNaN(costPerKm) || costPerKm <= 0) {
      return null;
    }
    
    const estimatedEarnings = distanceKm * costPerKm * maxPassengers;
    return estimatedEarnings;
  };

  // Function to calculate actual driver earnings based on rider carpool costs
  const calculateActualDriverEarnings = (trip) => {
    // If no carpool stops, return 0
    if (!trip.carpool_stops || !Array.isArray(trip.carpool_stops) || trip.carpool_stops.length === 0) {
      return 0;
    }

    // Group by driver post Trip ID and rider name, then get max RiderCarpoolCost for each group
    const riderGroups = new Map();
    
    trip.carpool_stops.forEach(stop => {
      if (stop.RiderName && stop.RiderCarpoolCost !== undefined && stop.RiderCarpoolCost !== null) {
        const key = `${trip.driverPostsTrip_id || trip.tripID || 'unknown'}_${stop.RiderName}`;
        const cost = Number(stop.RiderCarpoolCost);
        
        if (!Number.isNaN(cost) && cost > 0) {
          // Get max cost for this rider group
          if (!riderGroups.has(key) || riderGroups.get(key) < cost) {
            riderGroups.set(key, cost);
          }
        }
      }
    });

    // Sum all the max costs
    let totalEarnings = 0;
    riderGroups.forEach(cost => {
      totalEarnings += cost;
    });

    return totalEarnings;
  };

  /**
   * Opens the modal with trip details using the data from the card object.
   *
   * @param {Object} trip – The trip object from the list view.
   */
  const handleMoreDetailsClick = async (trip) => {
    console.log("More Details clicked for upcoming trip:", trip);
    setPopupTripDetails(null); // Clear previous details
    setShowDetailsModal(true); // Open modal immediately with loading state

    try {
      if (selectedTab === 'driver') {
        // For driver trips, use the data directly from upcomingTripResultsDriver
        // No need for API call since all data is already available
        console.log("Driver trip data:", trip);
        setPopupTripDetails(trip);
      } else {
        // For rider trips, use the entire trip object to access both trip_summary and transit_times
        // No need for API call since all data is already available
        console.log("Rider trip data:", trip);
        console.log("Rider trip_summary:", trip.trip_summary);
        console.log("Rider transit_times:", trip.transit_times);
        console.log("Driver name:", trip.trip_summary?.driverName);
        console.log("Driver email:", trip.trip_summary?.driverEmail);
        setPopupTripDetails(trip);
      }
    } catch (error) {
      console.error('Error loading trip details:', error);
      // Set an error message to display in the modal
      setPopupTripDetails(selectedTab === 'driver'
        ? { "Error": "Failed to load driver trip details. Please try again." }
        : { "Error": "Failed to load rider trip details. Please try again." }
      );
    }
  };

  /**
   * Closes the trip details modal and resets stored state.
   */
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setPopupTripDetails(null); // Clear details when closing
  };

  /**
   * Shows the second-step confirmation alert for cancellations.
   */
  const handleCancelTrip = () => {
    console.log("Cancel Trip button clicked. Showing confirmation.");
    setShowConfirmationAlert(true); // Show the confirmation alert
  };

  /**
   * Confirms a cancellation with proper error handling and loading states.
   */
  const handleConfirmCancel = async () => {
    console.log("Cancellation confirmed for trip:", popupTripDetails);
    
    // Close confirmation alert and show loading
    setShowConfirmationAlert(false);
    setIsCancelling(true);

    try {
      // Validate required data based on trip type
      let tripId, endpoint, tripDataToSend;
      
      if (selectedTab === 'driver') {
        // Driver trip cancellation
        if (!popupTripDetails || !popupTripDetails.driverPostsTrip_id) {
          throw new Error("Missing trip information. Cannot cancel trip.");
        }
        endpoint = `${API_URL}/cancelupcomingdriver`;
        tripDataToSend = {
          driverPostsTrip_id: popupTripDetails.driverPostsTrip_id,
          driverOrigin_latitude: popupTripDetails.driverOrigin_latitude,
          driverOrigin_longitude: popupTripDetails.driverOrigin_longitude
        };
      } else {
        // Rider trip cancellation
        if (!popupTripDetails || !popupTripDetails.trip_summary?.tripID) {
          throw new Error("Missing trip information. Cannot cancel trip.");
        }
        endpoint = `${API_URL}/cancelupcomingrider`;
        tripDataToSend = popupTripDetails; // Send the entire trip object
      }

      console.log("Sending cancellation request:", tripDataToSend);
      const response = await axios.post(endpoint, tripDataToSend, {
        timeout: 10000 // 10 second timeout
      });
      
      console.log("Cancel trip response:", response.data);

      // Check if cancellation was successful
      const isSuccess = response.data && (
        response.data.success || 
        response.data.status === "success"
      );
      
      if (isSuccess) {
        // Show success alert
        setShowSuccessAlert(true);

        // Close the details modal
        closeDetailsModal();

        // TODO: Implement refresh logic or update local state to remove cancelled trip
        // For now, the trip will still show until the page is refreshed
      } else {
        // Handle API error response
        const errorMsg = response.data?.message || response.data?.error || "Failed to cancel trip. Please try again.";
        setErrorMessage(errorMsg);
        setShowErrorAlert(true);
      }
    } catch (error) {
      console.error('Error cancelling trip:', error);
      
      let errorMsg = "An unexpected error occurred. Please try again.";
      
      if (error.code === 'ECONNABORTED') {
        errorMsg = "Request timed out. Please check your connection and try again.";
      } else if (error.response) {
        // Server responded with error status
        errorMsg = error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Network error
        errorMsg = "Network error. Please check your connection and try again.";
      } else if (error.message) {
        // Other error
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
      setShowErrorAlert(true);
    } finally {
      // Always stop loading
      setIsCancelling(false);
    }
  };

  /**
   * User backed-out of cancellation; close confirmation alert.
   */
  const handleCancelConfirmation = () => {
    console.log("Cancellation confirmation cancelled.");
    setShowConfirmationAlert(false); // Close the confirmation alert
  };

  /**
   * Handle success alert dismissal and redirect to home.
   */
  const handleSuccessAlertDismiss = () => {
    setShowSuccessAlert(false);
    // Navigate back to home page after successful cancellation
    navigate('/home', { 
      state: { 
        user: user 
      } 
    });
  };

  /**
   * Handle error alert dismissal.
   */
  const handleErrorAlertDismiss = () => {
    setShowErrorAlert(false);
    setErrorMessage('');
  };

  return (
    <IonPage className="upcoming-trips-page">
      <IonContent fullscreen className="upcoming-trips-content">
        <div className="content-container">
          {/* Header with Back Button */}
          <div className="user-header">
            <IonButton
              fill="clear"
              className="back-button"
              onClick={() => navigate(-1)}
            >
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </div>

          {/* Page Title */}
          <div className="page-title-section">
            <h2 className="page-title">Upcoming Trips</h2>
          </div>

          {/* Tab Segment */}
          <IonSegment
            value={selectedTab}
            onIonChange={e => setSelectedTab(e.detail.value)}
            className="trips-segment"
          >
            <IonSegmentButton value="driver">
              <IonLabel>As Driver</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="passenger">
              <IonLabel>As Passenger</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {/* Trip Content */}
          <div className="tab-content-ionic">
            {selectedTab === 'driver' ? (
              <div>

                <IonList className="trip-list-ionic">
                  {validDriverTrips.length > 0 ? (
                    validDriverTrips.map((trip, index) => (
                      <div 
                        key={index} 
                        className={`trip-card ${!trip.cancelled ? 'clickable-card' : ''}`}
                        onClick={!trip.cancelled ? () => handleMoreDetailsClick(trip) : undefined}
                      >
                        <div className="card-header">
                          <div className="driver-info">
                            <span className="driver-name">{formatLocalDate(trip.TripDeparture_DateTime)}</span>
                            <span className={`driver-tier ${trip.cancelled ? 'cancelled-tier' : 'upcoming-tier'}`}>
                              {trip.cancelled ? 'CANCELLED' : 'UPCOMING'}
                            </span>
                          </div>
                          <span className="trip-price">
                            {calculateActualDriverEarnings(trip) > 0 ? `$${formatNumber(calculateActualDriverEarnings(trip))}` : 'No riders'}
                          </span>
                        </div>

                        {/* Origin Stop */}
                        <div className="origin-stop">
                          <div className="origin-circle"></div>
                          <span className="origin-time">{formatTime(trip.TripDeparture_DateTime)}</span>
                          <span className="origin-location">{trip.driverOriginName}</span>
                        </div>
                        
                        <div className="vertical-line"></div>

                        {/* Carpool Stops */}
                        {trip.carpool_stops && trip.carpool_stops.length > 0 &&
                          trip.carpool_stops.map((stop, stopIndex) => (
                            <div key={stopIndex}>
                              <div className="pickup-stop">
                                <div className="pickup-circle"></div>
                                <span className="pickup-time">{formatTime(stop.stopArrivalTime)}</span>
                                <span className="pickup-location">{stop.WayPointName}</span>
                              </div>
                              <div className="vertical-line"></div>
                            </div>
                          ))
                        }

                        {/* Destination Stop */}
                        <div className="destination-stop">
                          <div className="destination-circle"></div>
                          <span className="destination-time">{formatTime(trip.TripArrival_DateTime)}</span>
                          <span className="destination-location">{trip.driverDestinationName}</span>
                        </div>

                        <div className="separator"></div>

                        <div className="card-footer">
                          {!trip.cancelled && (
                            <div className="details-btn">
                              Details
                              <IonIcon icon={arrowBack} className="arrow" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <IonCard className="no-trips-card">
                      <IonCardContent>
                        <IonText className="no-trips-message">
                          <p>No upcoming trips found.</p>
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                  )}
                </IonList>
              </div>
            ) : (
              <IonList className="trip-list-ionic">
                {validPassengerTrips.length > 0 ? (
                  validPassengerTrips.map((trip, index) => (
                    <div 
                      key={index} 
                      className={`trip-card ${!trip.trip_summary?.RiderTripCancelled ? 'clickable-card' : ''}`}
                      onClick={!trip.trip_summary?.RiderTripCancelled ? () => handleMoreDetailsClick(trip) : undefined}
                    >
                      <div className="card-header">
                        <div className="driver-info passenger-driver-info">
                          <div className="trip-date-status">
                            <span className="trip-date">{formatLocalDate(trip.trip_summary?.driverTripDeparture_DateTime)}</span>
                            <span className={`driver-tier ${trip.trip_summary?.RiderTripCancelled ? 'cancelled-tier' : 'upcoming-tier'}`}>
                              {trip.trip_summary?.RiderTripCancelled ? 'CANCELLED' : 'UPCOMING'}
                            </span>
                          </div>
                          <div className="driver-name-row">
                            <span className="driver-name">{trip.trip_summary?.driverName || 'Driver'}</span>
                            <IonIcon icon={shield} className="shield-icon" />
                            <span className={`driver-tier ${trip.trip_summary?.RiderTripCancelled ? 'cancelled-tier' : 'verified-tier'}`}>Verified</span>
                          </div>
                        </div>
                        <span className="trip-price">
                          {trip.trip_summary?.TotalCost ? `$${formatNumber(trip.trip_summary.TotalCost)}` : '—'}
                        </span>
                      </div>

                      {/* Start Location */}
                      <div className="origin-stop">
                        <div className="origin-circle"></div>
                        <span className="origin-time">
                          {trip.transit_times?.start_to_pickup?.departureFromStart 
                            ? formatTime(trip.transit_times.start_to_pickup.departureFromStart)
                            : trip.trip_summary?.RiderTripCancelled ? '' : 'Time TBD'
                          }
                        </span>
                        <span className="origin-location">{trip.trip_summary?.RiderStartLocName || 'N/A'}</span>
                      </div>
                      
                      <div className="vertical-line"></div>
                      
                      {/* Pickup Location */}
                      <div className="pickup-stop">
                        <div className="pickup-circle"></div>
                        <span className="pickup-time">
                          {trip.transit_times?.start_to_pickup?.arrivalAtPickup 
                            ? formatTime(trip.transit_times.start_to_pickup.arrivalAtPickup)
                            : trip.trip_summary?.RiderTripCancelled ? '' : 'Time TBD'
                          }
                        </span>
                        <span className="pickup-location">{trip.trip_summary?.driverOriginName || 'N/A'}</span>
                      </div>
                      
                      <div className="vertical-line"></div>
                      
                      {/* Drop-off Location */}
                      <div className="dropoff-stop">
                        <div className="dropoff-triangle"></div>
                        <span className="dropoff-time">
                          {trip.transit_times?.dropoff_to_destination?.departureFromDropoff 
                            ? formatTime(trip.transit_times.dropoff_to_destination.departureFromDropoff)
                            : trip.trip_summary?.RiderTripCancelled ? '' : 'Time TBD'
                          }
                        </span>
                        <span className="dropoff-location">{trip.trip_summary?.driverDestinationName || 'N/A'}</span>
                      </div>
                      
                      <div className="vertical-line"></div>
                      
                      {/* End Location */}
                      <div className="destination-stop">
                        <div className="destination-circle"></div>
                        <span className="destination-time">
                          {trip.transit_times?.dropoff_to_destination?.arrivalAtDestination 
                            ? formatTime(trip.transit_times.dropoff_to_destination.arrivalAtDestination)
                            : trip.trip_summary?.RiderTripCancelled ? '' : 'Time TBD'
                          }
                        </span>
                        <span className="destination-location">{trip.trip_summary?.RiderEndLocName || 'N/A'}</span>
                      </div>

                      <div className="separator"></div>

                      <div className="card-footer">
                        {!trip.trip_summary?.RiderTripCancelled && (
                          <div className="details-btn">
                            Details
                            <IonIcon icon={arrowBack} className="arrow" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <IonCard className="no-trips-card">
                    <IonCardContent>
                      <IonText className="no-trips-message">
                        <p>No upcoming trips found.</p>
                      </IonText>
                    </IonCardContent>
                  </IonCard>
                )}
              </IonList>
            )}
          </div>
        </div>

        {/* Trip Details Modal */}
        <IonModal isOpen={showDetailsModal} onDidDismiss={closeDetailsModal} className="trip-details-modal">
          <IonHeader>
            <IonToolbar color="dark">
              <IonTitle>Trip Details</IonTitle>
              <IonButton
                fill="clear"
                slot="end"
                onClick={closeDetailsModal}
                className="modal-close-button"
              >
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent scrollY={false}>
            <div className="trip-details-popup-content">
              {/* Trip Details Content - ViewResultAsRider Style */}
              {popupTripDetails && typeof popupTripDetails === 'object' ? (
                <div className="trip-details-popup-container">
                  {/* Page Title */}
                  <div className="popup-title-section">
                    <h2 className="popup-title">Trip Details</h2>
                  </div>

                  <div className="popup-date-display">
                    {formatShortDate(popupTripDetails.TripDeparture_DateTime || popupTripDetails.driverTripDeparture_DateTime || popupTripDetails.trip_summary?.driverTripDeparture_DateTime)}
                  </div>

                  {/* Driver Card - Only show for rider trips */}
                  {selectedTab === 'passenger' && (
                    <div className="driver-card">
                      <div className="driver-info-compact">
                        <IonAvatar className="driver-avatar-compact">
                          <img
                            src="/default-avatar.png"
                            alt="Driver"
                            crossOrigin="anonymous"
                          />
                        </IonAvatar>
                        <div className="driver-details-compact">
                          <span className="driver-name-compact">
                            {popupTripDetails.driverName || popupTripDetails.trip_summary?.driverName || 'N/A'}
                          </span>
                          <IonIcon icon={shield} className="shield-icon-compact" />
                          <span className="driver-tier-compact">Verified</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Driver Contact Details Section - Only show for passenger trips */}
                  {selectedTab === 'passenger' && (
                    <div className="price-card">
                      <div className="price-details">
                        <span className="price-label">Driver Contact Details</span>
                        <div className="contact-details">
                          <span className="driver-email">
                            {popupTripDetails.driverEmail || popupTripDetails.trip_summary?.driverEmail || 'N/A'}
                          </span>
                          {(popupTripDetails.driverPhoneNumber || popupTripDetails.trip_summary?.driverPhoneNumber) && (
                            <span className="driver-phone">
                              {popupTripDetails.driverPhoneNumber || popupTripDetails.trip_summary?.driverPhoneNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Price/Earnings Card */}
                  <div className="price-card">
                    <div className="price-details">
                      <span className="price-label">
                        {selectedTab === 'driver' ? 'Pending Earnings' : 'Price'}
                      </span>
                      <span className="price-value">
                        {selectedTab === 'driver' ? (
                          calculateActualDriverEarnings(popupTripDetails) > 0 ? 
                            `$${formatNumber(calculateActualDriverEarnings(popupTripDetails))}` : 'No riders'
                        ) : (
                          popupTripDetails.TotalCost ? `$${formatNumber(popupTripDetails.TotalCost)}` : 
                          popupTripDetails.trip_summary?.TotalCost ? `$${formatNumber(popupTripDetails.trip_summary.TotalCost)}` :
                          popupTripDetails.costOfLeg ? `$${formatNumber(popupTripDetails.costOfLeg)}` : 'N/A'
                        )}
                      </span>
                    </div>
                  </div>

                  {/* View Entire Trip Card - Only show for driver trips */}
                  {selectedTab === 'driver' && (
                    <div 
                      className="view-trip-card clickable-card"
                      onClick={() => {
                        // Build Google Maps URL with all stops
                        const stops = [];
                        
                        // Add origin
                        if (popupTripDetails.driverOrigin_latitude && popupTripDetails.driverOrigin_longitude) {
                          stops.push(`${popupTripDetails.driverOrigin_latitude},${popupTripDetails.driverOrigin_longitude}`);
                        }
                        
                        // Add carpool stops
                        if (popupTripDetails.carpool_stops && popupTripDetails.carpool_stops.length > 0) {
                          popupTripDetails.carpool_stops.forEach(stop => {
                            if (stop.WayPoint_latitude && stop.WayPoint_longitude) {
                              stops.push(`${stop.WayPoint_latitude},${stop.WayPoint_longitude}`);
                            }
                          });
                        }
                        
                        // Add destination
                        if (popupTripDetails.driverDestination_latitude && popupTripDetails.driverDestination_longitude) {
                          stops.push(`${popupTripDetails.driverDestination_latitude},${popupTripDetails.driverDestination_longitude}`);
                        }
                        
                        // Create Google Maps URL with all stops
                        if (stops.length > 0) {
                          const mapsUrl = `https://www.google.com/maps/dir/${stops.join('/')}`;
                          window.open(mapsUrl, '_blank');
                        }
                      }}
                    >
                      <div className="view-trip-content">
                        <IonIcon icon={navigateOutline} className="view-trip-icon" />
                        <span className="view-trip-text">View Entire Trip</span>
                        <IonIcon icon={arrowBack} className="view-trip-arrow" />
                      </div>
                    </div>
                  )}

                  {/* Route Card */}
                  <div className="route-card">
                    <div className="route-details-compact">
                      {selectedTab === 'driver' ? (
                        <>
                          {/* Origin Stop - matches driver card */}
                          <div className="route-stop-compact">
                            <div className="origin-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Origin</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.driverOriginName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>{formatTime(popupTripDetails.TripDeparture_DateTime)}</IonNote>
                              </div>
                            </div>
                            <div className="stop-actions">
                              {popupTripDetails.driverOrigin_latitude && popupTripDetails.driverOrigin_longitude && (
                                <button 
                                  className="copy-coordinates-btn"
                                  onClick={() => navigator.clipboard.writeText(`${popupTripDetails.driverOrigin_latitude}, ${popupTripDetails.driverOrigin_longitude}`)}
                                  title="Copy Coordinates"
                                >
                                  <IonIcon icon={copyOutline} className="copy-icon" />
                                </button>
                              )}
                              {/* Navigation button to next stop */}
                              {((popupTripDetails.carpool_stops && popupTripDetails.carpool_stops.length > 0) || popupTripDetails.driverDestination_latitude) && (
                                <button 
                                  className="maps-navigation-btn"
                                  onClick={() => {
                                    const nextStop = popupTripDetails.carpool_stops && popupTripDetails.carpool_stops.length > 0 
                                      ? popupTripDetails.carpool_stops[0]
                                      : { WayPoint_latitude: popupTripDetails.driverDestination_latitude, WayPoint_longitude: popupTripDetails.driverDestination_longitude };
                                    const mapsUrl = `https://www.google.com/maps/dir/${popupTripDetails.driverOrigin_latitude},${popupTripDetails.driverOrigin_longitude}/${nextStop.WayPoint_latitude},${nextStop.WayPoint_longitude}`;
                                    window.open(mapsUrl, '_blank');
                                  }}
                                  title="Navigate to next stop"
                                >
                                  <IonIcon icon={navigateOutline} className="maps-icon" />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="vertical-line"></div>

                          {/* Carpool Stops - matches driver card */}
                          {popupTripDetails.carpool_stops && popupTripDetails.carpool_stops.length > 0 &&
                            popupTripDetails.carpool_stops.map((stop, stopIndex) => {
                              const isLastCarpoolStop = stopIndex === popupTripDetails.carpool_stops.length - 1;
                              const hasNextStop = !isLastCarpoolStop || popupTripDetails.driverDestination_latitude;
                              
                              return (
                                <div key={stopIndex}>
                                  <div className="route-stop-compact">
                                    <div className="pickup-circle"></div>
                                    <div className="stop-content-compact">
                                      <span className="stop-label-compact">
                                        Carpool Stop {stop.stopOrder || stopIndex + 1}
                                        {stop.RiderName && `: ${stop.RiderName}`}
                                      </span>
                                      <span className="stop-address-compact">
                                        {stop.WayPointName || 'N/A'}
                                      </span>
                                      <div className="arrival-time-compact">
                                        <IonNote>{formatTime(stop.stopArrivalTime)}</IonNote>
                                      </div>
                                    </div>
                                    <div className="stop-actions">
                                      {stop.WayPoint_latitude && stop.WayPoint_longitude && (
                                        <button 
                                          className="copy-coordinates-btn"
                                          onClick={() => navigator.clipboard.writeText(`${stop.WayPoint_latitude}, ${stop.WayPoint_longitude}`)}
                                          title="Copy Coordinates"
                                        >
                                          <IonIcon icon={copyOutline} className="copy-icon" />
                                        </button>
                                      )}
                                      {/* Navigation button to next stop */}
                                      {hasNextStop && stop.WayPoint_latitude && stop.WayPoint_longitude && (
                                        <button 
                                          className="maps-navigation-btn"
                                          onClick={() => {
                                            const nextStop = !isLastCarpoolStop 
                                              ? popupTripDetails.carpool_stops[stopIndex + 1]
                                              : { WayPoint_latitude: popupTripDetails.driverDestination_latitude, WayPoint_longitude: popupTripDetails.driverDestination_longitude };
                                            const mapsUrl = `https://www.google.com/maps/dir/${stop.WayPoint_latitude},${stop.WayPoint_longitude}/${nextStop.WayPoint_latitude},${nextStop.WayPoint_longitude}`;
                                            window.open(mapsUrl, '_blank');
                                          }}
                                          title="Navigate to next stop"
                                        >
                                          <IonIcon icon={navigateOutline} className="maps-icon" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="vertical-line"></div>
                                </div>
                              );
                            })
                          }

                          {/* Destination Stop - matches driver card */}
                          <div className="route-stop-compact">
                            <div className="destination-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Destination</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.driverDestinationName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>{formatTime(popupTripDetails.TripArrival_DateTime)}</IonNote>
                              </div>
                            </div>
                            <div className="stop-actions">
                              {popupTripDetails.driverDestination_latitude && popupTripDetails.driverDestination_longitude && (
                                <button 
                                  className="copy-coordinates-btn"
                                  onClick={() => navigator.clipboard.writeText(`${popupTripDetails.driverDestination_latitude}, ${popupTripDetails.driverDestination_longitude}`)}
                                  title="Copy Coordinates"
                                >
                                  <IonIcon icon={copyOutline} className="copy-icon" />
                                </button>
                              )}
                              {/* No navigation button for destination - it's the last stop */}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Rider trip stops - keep existing structure */}
                          {/* Start */}
                          <div className="route-stop-compact">
                            <div className="origin-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Start</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.RiderStartLocName || popupTripDetails.trip_summary?.RiderStartLocName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>
                                  {popupTripDetails.transit_times?.start_to_pickup?.departureFromStart 
                                    ? `Depart by: ${formatTime(popupTripDetails.transit_times.start_to_pickup.departureFromStart)}`
                                    : popupTripDetails.RiderTripCancelled || popupTripDetails.trip_summary?.RiderTripCancelled ? '' : 'Depart by: Time TBD'
                                  }
                                </IonNote>
                              </div>
                            </div>
                            <div className="stop-actions">
                              {(popupTripDetails.RiderStartLat && popupTripDetails.RiderStartLong) || (popupTripDetails.trip_summary?.RiderStartLat && popupTripDetails.trip_summary?.RiderStartLong) ? (
                                <button 
                                  className="copy-coordinates-btn"
                                  onClick={() => navigator.clipboard.writeText(`${popupTripDetails.RiderStartLat || popupTripDetails.trip_summary?.RiderStartLat}, ${popupTripDetails.RiderStartLong || popupTripDetails.trip_summary?.RiderStartLong}`)}
                                  title="Copy Coordinates"
                                >
                                  <IonIcon icon={copyOutline} className="copy-icon" />
                                </button>
                              ) : null}
                              {/* Navigation button to pickup location */}
                              {((popupTripDetails.driverOrigin_latitude && popupTripDetails.driverOrigin_longitude) || (popupTripDetails.trip_summary?.driverOrigin_latitude && popupTripDetails.trip_summary?.driverOrigin_longitude)) && 
                               ((popupTripDetails.RiderStartLat && popupTripDetails.RiderStartLong) || (popupTripDetails.trip_summary?.RiderStartLat && popupTripDetails.trip_summary?.RiderStartLong)) && (
                                <button 
                                  className="maps-navigation-btn"
                                  onClick={() => {
                                    const startLat = popupTripDetails.RiderStartLat || popupTripDetails.trip_summary?.RiderStartLat;
                                    const startLong = popupTripDetails.RiderStartLong || popupTripDetails.trip_summary?.RiderStartLong;
                                    const pickupLat = popupTripDetails.driverOrigin_latitude || popupTripDetails.trip_summary?.driverOrigin_latitude;
                                    const pickupLong = popupTripDetails.driverOrigin_longitude || popupTripDetails.trip_summary?.driverOrigin_longitude;
                                    const mapsUrl = `https://www.google.com/maps/dir/${startLat},${startLong}/${pickupLat},${pickupLong}`;
                                    window.open(mapsUrl, '_blank');
                                  }}
                                  title="Navigate to pickup location"
                                >
                                  <IonIcon icon={navigateOutline} className="maps-icon" />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="vertical-line"></div>

                          {/* Pickup */}
                          <div className="route-stop-compact">
                            <div className="pickup-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Pickup</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.driverOriginName || popupTripDetails.trip_summary?.driverOriginName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>
                                  {popupTripDetails.transit_times?.start_to_pickup?.arrivalAtPickup 
                                    ? `Arrive by: ${formatTime(popupTripDetails.transit_times.start_to_pickup.arrivalAtPickup)}`
                                    : popupTripDetails.RiderTripCancelled || popupTripDetails.trip_summary?.RiderTripCancelled ? '' : 'Arrive by: Time TBD'
                                  }
                                </IonNote>
                              </div>
                            </div>
                            <div className="stop-actions">
                              {(popupTripDetails.driverOrigin_latitude && popupTripDetails.driverOrigin_longitude) || (popupTripDetails.trip_summary?.driverOrigin_latitude && popupTripDetails.trip_summary?.driverOrigin_longitude) ? (
                                <button 
                                  className="copy-coordinates-btn"
                                  onClick={() => navigator.clipboard.writeText(`${popupTripDetails.driverOrigin_latitude || popupTripDetails.trip_summary?.driverOrigin_latitude}, ${popupTripDetails.driverOrigin_longitude || popupTripDetails.trip_summary?.driverOrigin_longitude}`)}
                                  title="Copy Coordinates"
                                >
                                  <IonIcon icon={copyOutline} className="copy-icon" />
                                </button>
                              ) : null}
                              {/* Navigation button to drop-off location */}
                              {((popupTripDetails.driverDestination_latitude && popupTripDetails.driverDestination_longitude) || (popupTripDetails.trip_summary?.driverDestination_latitude && popupTripDetails.trip_summary?.driverDestination_longitude)) && 
                               ((popupTripDetails.driverOrigin_latitude && popupTripDetails.driverOrigin_longitude) || (popupTripDetails.trip_summary?.driverOrigin_latitude && popupTripDetails.trip_summary?.driverOrigin_longitude)) && (
                                <button 
                                  className="maps-navigation-btn"
                                  onClick={() => {
                                    const pickupLat = popupTripDetails.driverOrigin_latitude || popupTripDetails.trip_summary?.driverOrigin_latitude;
                                    const pickupLong = popupTripDetails.driverOrigin_longitude || popupTripDetails.trip_summary?.driverOrigin_longitude;
                                    const dropoffLat = popupTripDetails.driverDestination_latitude || popupTripDetails.trip_summary?.driverDestination_latitude;
                                    const dropoffLong = popupTripDetails.driverDestination_longitude || popupTripDetails.trip_summary?.driverDestination_longitude;
                                    const mapsUrl = `https://www.google.com/maps/dir/${pickupLat},${pickupLong}/${dropoffLat},${dropoffLong}`;
                                    window.open(mapsUrl, '_blank');
                                  }}
                                  title="Navigate to drop-off location"
                                >
                                  <IonIcon icon={navigateOutline} className="maps-icon" />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="vertical-line"></div>

                          {/* Drop-off */}
                          <div className="route-stop-compact">
                            <div className="dropoff-triangle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Drop-off</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.driverDestinationName || popupTripDetails.trip_summary?.driverDestinationName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>
                                  {popupTripDetails.transit_times?.dropoff_to_destination?.departureFromDropoff 
                                    ? `Arrive by: ${formatTime(popupTripDetails.transit_times.dropoff_to_destination.departureFromDropoff)}`
                                    : popupTripDetails.RiderTripCancelled || popupTripDetails.trip_summary?.RiderTripCancelled ? '' : 'Arrive by: Time TBD'
                                  }
                                </IonNote>
                              </div>
                            </div>
                            <div className="stop-actions">
                              {(popupTripDetails.driverDestination_latitude && popupTripDetails.driverDestination_longitude) || (popupTripDetails.trip_summary?.driverDestination_latitude && popupTripDetails.trip_summary?.driverDestination_longitude) ? (
                                <button 
                                  className="copy-coordinates-btn"
                                  onClick={() => navigator.clipboard.writeText(`${popupTripDetails.driverDestination_latitude || popupTripDetails.trip_summary?.driverDestination_latitude}, ${popupTripDetails.driverDestination_longitude || popupTripDetails.trip_summary?.driverDestination_longitude}`)}
                                  title="Copy Coordinates"
                                >
                                  <IonIcon icon={copyOutline} className="copy-icon" />
                                </button>
                              ) : null}
                              {/* Navigation button to final destination */}
                              {((popupTripDetails.RiderEndLat && popupTripDetails.RiderEndLong) || (popupTripDetails.trip_summary?.RiderEndLat && popupTripDetails.trip_summary?.RiderEndLong)) && 
                               ((popupTripDetails.driverDestination_latitude && popupTripDetails.driverDestination_longitude) || (popupTripDetails.trip_summary?.driverDestination_latitude && popupTripDetails.trip_summary?.driverDestination_longitude)) && (
                                <button 
                                  className="maps-navigation-btn"
                                  onClick={() => {
                                    const dropoffLat = popupTripDetails.driverDestination_latitude || popupTripDetails.trip_summary?.driverDestination_latitude;
                                    const dropoffLong = popupTripDetails.driverDestination_longitude || popupTripDetails.trip_summary?.driverDestination_longitude;
                                    const endLat = popupTripDetails.RiderEndLat || popupTripDetails.trip_summary?.RiderEndLat;
                                    const endLong = popupTripDetails.RiderEndLong || popupTripDetails.trip_summary?.RiderEndLong;
                                    const mapsUrl = `https://www.google.com/maps/dir/${dropoffLat},${dropoffLong}/${endLat},${endLong}`;
                                    window.open(mapsUrl, '_blank');
                                  }}
                                  title="Navigate to final destination"
                                >
                                  <IonIcon icon={navigateOutline} className="maps-icon" />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <div className="vertical-line"></div>

                          {/* Destination */}
                          <div className="route-stop-compact">
                            <div className="destination-circle"></div>
                            <div className="stop-content-compact">
                              <span className="stop-label-compact">Destination</span>
                              <span className="stop-address-compact">
                                {popupTripDetails.RiderEndLocName || popupTripDetails.trip_summary?.RiderEndLocName || 'N/A'}
                              </span>
                              <div className="arrival-time-compact">
                                <IonNote>
                                  {popupTripDetails.transit_times?.dropoff_to_destination?.arrivalAtDestination 
                                    ? `Arrive by: ${formatTime(popupTripDetails.transit_times.dropoff_to_destination.arrivalAtDestination)}`
                                    : popupTripDetails.RiderTripCancelled || popupTripDetails.trip_summary?.RiderTripCancelled ? '' : 'Arrive by: Time TBD'
                                  }
                                </IonNote>
                              </div>
                            </div>
                            <div className="stop-actions">
                              {(popupTripDetails.RiderEndLat && popupTripDetails.RiderEndLong) || (popupTripDetails.trip_summary?.RiderEndLat && popupTripDetails.trip_summary?.RiderEndLong) ? (
                                <button 
                                  className="copy-coordinates-btn"
                                  onClick={() => navigator.clipboard.writeText(`${popupTripDetails.RiderEndLat || popupTripDetails.trip_summary?.RiderEndLat}, ${popupTripDetails.RiderEndLong || popupTripDetails.trip_summary?.RiderEndLong}`)}
                                  title="Copy Coordinates"
                                >
                                  <IonIcon icon={copyOutline} className="copy-icon" />
                                </button>
                              ) : null}
                              {/* No navigation button for destination - it's the final stop */}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Passengers Card - Only show for driver trips */}
                  {selectedTab === 'driver' && (
                    <div className="passengers-card">
                      <div className="passengers-header">
                        <IonIcon icon={personOutline} className="passengers-icon" />
                        <span className="passengers-title">Passengers</span>
                      </div>
                      <div className="passengers-content">
                        {popupTripDetails.carpool_stops && popupTripDetails.carpool_stops.length > 0 ? (
                          <div className="passengers-list">
                            {(() => {
                              // Get unique rider names from carpool_stops
                              const uniqueRiders = [];
                              const seenRiders = new Set();
                              
                              popupTripDetails.carpool_stops.forEach(stop => {
                                if (stop.RiderName && !seenRiders.has(stop.RiderName)) {
                                  seenRiders.add(stop.RiderName);
                                  uniqueRiders.push({
                                    name: stop.RiderName,
                                    email: stop.riderEmail,
                                    phone: stop.riderPhoneNumber
                                  });
                                }
                              });
                              
                              return uniqueRiders.map((rider, index) => (
                                <div key={index} className="passenger-item">
                                  <div className="passenger-info">
                                    <span className="passenger-name">{rider.name}</span>
                                    {rider.email && (
                                      <span className="passenger-email">{rider.email}</span>
                                    )}
                                    {rider.phone && (
                                      <span className="passenger-phone">{rider.phone}</span>
                                    )}
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        ) : (
                          <div className="no-passengers">
                            <span className="no-passengers-text">No passengers yet for this trip</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Trip Statistics Card */}
                  <div className="stats-card">
                    <div className="stats-grid">
                      <div className="stat-item">
                        <span className="stat-label">Total Distance</span>
                        <span className="stat-value">
                          {selectedTab === 'driver' ? (
                            popupTripDetails.TotalDistance ? `${formatNumber(popupTripDetails.TotalDistance / 1000, 1)} km` : 'N/A'
                          ) : (
                            popupTripDetails.riderCarpoolDist ? `${formatNumber(popupTripDetails.riderCarpoolDist, 1)} km` : 
                            popupTripDetails.trip_summary?.riderCarpoolDist ? `${formatNumber(popupTripDetails.trip_summary.riderCarpoolDist, 1)} km` : 'N/A'
                          )}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Total Duration</span>
                        <span className="stat-value">
                          {selectedTab === 'driver' ? (
                            popupTripDetails.TripDeparture_DateTime && popupTripDetails.TripArrival_DateTime 
                              ? `${Math.round((new Date(popupTripDetails.TripArrival_DateTime) - new Date(popupTripDetails.TripDeparture_DateTime)) / (1000 * 60))} min`
                              : 'N/A'
                          ) : (
                            popupTripDetails.transit_times ? (() => {
                              const startToPickup = popupTripDetails.transit_times.start_to_pickup?.travelTimeSeconds || 0;
                              const dropoffToDestination = popupTripDetails.transit_times.dropoff_to_destination?.travelTimeSeconds || 0;
                              const totalMinutes = startToPickup + dropoffToDestination;
                              return totalMinutes > 0 ? `${totalMinutes} min` : 'N/A';
                            })() : 'N/A'
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Cancel Button - Keep existing functionality */}
                  {!popupTripDetails.cancelled && !popupTripDetails.RiderTripCancelled && !popupTripDetails.trip_summary?.RiderTripCancelled && (
                    <div className="cancel-button-container">
                      <IonButton
                        expand="block"
                        className="cancel-button-popup"
                        onClick={handleCancelTrip}
                        disabled={isCancelling}
                      >
                        {isCancelling ? (
                          <>
                            <IonSpinner name="crescent" slot="start" />
                            Cancelling...
                          </>
                        ) : (
                          <>
                            <IonIcon icon={alertCircleOutline} slot="start" />
                            CANCEL TRIP
                          </>
                        )}
                      </IonButton>
                    </div>
                  )}

                  {/* Handle error case */}
                  {popupTripDetails.Error && (
                    <div className="error-card">
                      <div className="error-content">
                        <IonIcon icon={closeCircleOutline} className="error-icon" color="danger" />
                        <IonText color="danger">
                          <p><strong>Error:</strong> {popupTripDetails.Error}</p>
                        </IonText>
                      </div>
                    </div>
                  )}
                </div>
              ) : popupTripDetails === null ? (
                <div className="loading-container">
                  <IonSpinner name="crescent" color="primary" />
                  <IonText>
                    <p>Loading trip details...</p>
                  </IonText>
                </div>
              ) : (
                <div className="error-card">
                  <div className="error-content">
                    <IonIcon icon={closeCircleOutline} className="error-icon" color="danger" />
                    <IonText color="danger">
                      <p>Error loading trip details. Unexpected data format.</p>
                    </IonText>
                  </div>
                </div>
              )}
            </div>
          </IonContent>
        </IonModal>

        {/* Cancellation Confirmation Alert */}
        <IonAlert
          isOpen={showConfirmationAlert}
          onDidDismiss={handleCancelConfirmation}
          header="Confirm Cancellation"
          message="Are you sure you want to cancel this trip? This action cannot be undone."
          buttons={[
            {
              text: 'No, Keep Trip',
              role: 'cancel',
              handler: handleCancelConfirmation
            },
            {
              text: 'Yes, Cancel',
              role: 'destructive',
              handler: handleConfirmCancel
            }
          ]}
        />

        {/* Success Alert */}
        <IonAlert
          isOpen={showSuccessAlert}
          onDidDismiss={handleSuccessAlertDismiss}
          header="Success"
          message="Trip cancelled successfully!"
          buttons={[
            {
              text: 'OK',
              handler: handleSuccessAlertDismiss
            }
          ]}
        />

        {/* Error Alert */}
        <IonAlert
          isOpen={showErrorAlert}
          onDidDismiss={handleErrorAlertDismiss}
          header="Error"
          message={errorMessage}
          buttons={[
            {
              text: 'OK',
              handler: handleErrorAlertDismiss
            }
          ]}
        />

      </IonContent>
    </IonPage>
  );
};

export default UpcomingTrips;